// pages/admin/TemplatesPage.tsx
/**
 * @description Template management page - list/create/edit unit templates for a building
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  ImageIcon,
  Layers,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal, Input, Select } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import {
  getBuilding,
  getUnitTypes,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type Building,
  type UnitType,
  type UnitTemplate
} from '../../lib/api';

export function TemplatesPage() {
  const { slug } = useParams<{ slug: string }>();
  const { showToast } = useToast();

  // Data state
  const [building, setBuilding] = useState<Building | null>(null);
  const [templates, setTemplates] = useState<UnitTemplate[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<UnitTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<UnitTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      const [buildingResponse, unitTypesResponse] = await Promise.all([
        getBuilding(slug),
        getUnitTypes()
      ]);

      if (buildingResponse.error) {
        setError(buildingResponse.error);
        setLoading(false);
        return;
      }

      if (buildingResponse.data) {
        setBuilding(buildingResponse.data.building);

        // Fetch templates for this building
        const templatesResponse = await getTemplates(buildingResponse.data.building.id);
        if (templatesResponse.data) {
          setTemplates(templatesResponse.data.templates);
        }
      }

      if (unitTypesResponse.data) {
        setUnitTypes(unitTypesResponse.data.unit_types);
      }

      setLoading(false);
    }

    fetchData();
  }, [slug]);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: UnitTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (template: UnitTemplate) => {
    setDeletingTemplate(template);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTemplate) return;

    setSaving(true);
    const { error } = await deleteTemplate(deletingTemplate.id);
    setSaving(false);

    if (error) {
      showToast(`Failed to delete: ${error}`, 'error');
    } else {
      setTemplates((prev) => prev.filter((t) => t.id !== deletingTemplate.id));
      showToast(`Template "${deletingTemplate.name}" deleted`, 'success');
      setIsDeleteModalOpen(false);
      setDeletingTemplate(null);
    }
  };

  const handleSaveTemplate = async (formData: TemplateFormData) => {
    if (!building) return;

    setSaving(true);

    if (editingTemplate) {
      // Update existing
      const { error } = await updateTemplate(editingTemplate.id, formData);
      setSaving(false);

      if (error) {
        showToast(`Failed to update: ${error}`, 'error');
        return;
      }

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === editingTemplate.id
            ? {
                ...t,
                ...formData,
                unit_type: unitTypes.find((ut) => ut.id === formData.unit_type_id) || null,
              }
            : t
        )
      );
      showToast(`Template "${formData.name}" updated`, 'success');
    } else {
      // Create new
      const { data, error } = await createTemplate(building.id, formData);
      setSaving(false);

      if (error) {
        showToast(`Failed to create: ${error}`, 'error');
        return;
      }

      if (data) {
        const newTemplate: UnitTemplate = {
          id: data.id,
          building_id: building.id,
          unit_type_id: formData.unit_type_id,
          unit_type: unitTypes.find((ut) => ut.id === formData.unit_type_id) || null,
          name: formData.name,
          description: formData.description || null,
          default_square_feet: formData.default_square_feet || null,
          default_features: formData.default_features || [],
          is_active: formData.is_active !== false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          images: [],
        };
        setTemplates((prev) => [...prev, newTemplate]);
        showToast(`Template "${formData.name}" created`, 'success');
      }
    }

    setIsModalOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Loading templates...</span>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !building) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Building Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'Unable to load building data.'}</p>
          <Link to="/admin" className="text-primary hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      </AdminLayout>
    );
  }

  // Group templates by unit type
  const templatesByType = templates.reduce((acc, t) => {
    const typeName = t.unit_type?.name || 'Other';
    if (!acc[typeName]) acc[typeName] = [];
    acc[typeName].push(t);
    return acc;
  }, {} as Record<string, UnitTemplate[]>);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to={`/admin/buildings/${slug}`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to {building.name}
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <div className="flex items-center gap-2">
              <Layers size={24} className="text-primary" />
              <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
                Unit Templates
              </h1>
            </div>
            <p className="text-sm lg:text-base text-gray-500 mt-1">
              {building.name} • {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={handleAddTemplate} className="w-full sm:w-auto">
            <Plus size={16} className="mr-2" />
            New Template
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Templates</strong> define reusable unit configurations with images and default features.
            Assign a template to units to share photos across similar units.
          </p>
        </div>

        {/* Templates List */}
        {templates.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Layers size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Yet</h3>
            <p className="text-gray-500 mb-4">
              Create templates to define unit configurations with images that can be reused across multiple units.
            </p>
            <Button onClick={handleAddTemplate}>
              <Plus size={16} className="mr-2" />
              Create First Template
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(templatesByType).map(([typeName, typeTemplates]) => (
              <div key={typeName}>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  {typeName}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {typeTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onEdit={() => handleEditTemplate(template)}
                      onDelete={() => handleDeleteClick(template)}
                      buildingSlug={slug!}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Template Modal */}
      <TemplateFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTemplate}
        template={editingTemplate}
        unitTypes={unitTypes}
        saving={saving}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Template"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Deleting...' : 'Delete Template'}
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-gray-900">
              Are you sure you want to delete <strong>"{deletingTemplate?.name}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-1">
              This will not delete any units, but they will lose their template assignment.
            </p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
}

// Template Card Component
interface TemplateCardProps {
  template: UnitTemplate;
  onEdit: () => void;
  onDelete: () => void;
  buildingSlug: string;
}

function TemplateCard({ template, onEdit, onDelete, buildingSlug }: TemplateCardProps) {
  const hasImages = template.images && template.images.length > 0;
  const firstImage = hasImages ? template.images[0] : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors">
      {/* Image Preview */}
      <div className="aspect-video bg-gray-100 relative">
        {firstImage ? (
          <img
            src={firstImage.image_url}
            alt={template.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
            <ImageIcon size={32} />
            <span className="text-xs mt-1">No images</span>
          </div>
        )}
        {hasImages && (
          <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
            {template.images.length} photo{template.images.length !== 1 ? 's' : ''}
          </span>
        )}
        {/* Status badge */}
        <span className={`absolute top-2 right-2 flex items-center gap-1 text-xs px-2 py-1 rounded ${
          template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {template.is_active ? (
            <>
              <CheckCircle2 size={12} />
              Active
            </>
          ) : (
            <>
              <XCircle size={12} />
              Inactive
            </>
          )}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <h4 className="font-medium text-gray-900">{template.name}</h4>
            <p className="text-sm text-gray-500">{template.unit_type?.name || 'No type'}</p>
          </div>
        </div>

        {template.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          {template.default_square_feet && (
            <span>{template.default_square_feet} sq ft</span>
          )}
          {template.default_features.length > 0 && (
            <span>{template.default_features.length} feature{template.default_features.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          <Link
            to={`/admin/buildings/${buildingSlug}/templates/${template.id}`}
            className="flex-1 py-2 text-sm font-medium text-center text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
          >
            Manage Images
          </Link>
          <button
            onClick={onEdit}
            className="p-2 text-gray-500 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Pencil size={16} />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Form types
interface TemplateFormData {
  unit_type_id: string;
  name: string;
  description?: string;
  default_square_feet?: number;
  default_features?: string[];
  is_active?: boolean;
}

interface TemplateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TemplateFormData) => void;
  template: UnitTemplate | null;
  unitTypes: UnitType[];
  saving: boolean;
}

function TemplateFormModal({ isOpen, onClose, onSave, template, unitTypes, saving }: TemplateFormModalProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    unit_type_id: template?.unit_type_id || '',
    name: template?.name || '',
    description: template?.description || '',
    default_square_feet: template?.default_square_feet || undefined,
    default_features: template?.default_features || [],
    is_active: template?.is_active ?? true,
  });
  const [featuresInput, setFeaturesInput] = useState('');

  // Reset form when template changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        unit_type_id: template?.unit_type_id || '',
        name: template?.name || '',
        description: template?.description || '',
        default_square_feet: template?.default_square_feet || undefined,
        default_features: template?.default_features || [],
        is_active: template?.is_active ?? true,
      });
      setFeaturesInput(template?.default_features?.join(', ') || '');
    }
  }, [isOpen, template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const features = featuresInput
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    onSave({ ...formData, default_features: features });
  };

  const unitTypeOptions = unitTypes.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Edit Template' : 'New Template'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-auto">
            {saving ? 'Saving...' : template ? 'Save Changes' : 'Create Template'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Unit Type"
          options={unitTypeOptions}
          placeholder="Select unit type"
          value={formData.unit_type_id}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, unit_type_id: e.target.value }))
          }
          required
        />

        <Input
          label="Template Name"
          placeholder="e.g. 1BR Corner, 2BR Renovated"
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          hint="A descriptive name for this unit configuration"
          required
        />

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            rows={2}
            placeholder="Optional description..."
            value={formData.description || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </div>

        <Input
          label="Default Square Feet (optional)"
          type="number"
          placeholder="650"
          value={formData.default_square_feet || ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              default_square_feet: e.target.value ? parseInt(e.target.value) : undefined,
            }))
          }
        />

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Default Features (optional)</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="Hardwood floors, Stainless appliances, Balcony"
            value={featuresInput}
            onChange={(e) => setFeaturesInput(e.target.value)}
          />
          <p className="text-xs text-gray-500">Comma-separated list of features</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
            }
            className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          Active (can be assigned to units)
        </label>
      </form>
    </Modal>
  );
}
